// FILE: ProductDetails/ProductDetails.jsx
// Fully responsive version with enhanced UI, proper loading states, and skeleton loaders

import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  FiEdit2,
  FiTrash2,
  FiDollarSign,
  FiMapPin,
  FiShield,
  FiPackage,
  FiTrendingUp,
  FiTrendingDown,
  FiClock,
  FiRefreshCw,
  FiCheckCircle,
  FiXCircle,
  FiInfo,
  FiPercent,
  FiTag,
  FiChevronDown,
  FiChevronUp,
  FiBox,
  FiGrid,
  FiLayers,
  FiStar,
  FiImage,
  FiArrowLeft,
  FiEye,
  FiSettings,
  FiUser,
  FiCalendar,
  FiFileText,
} from "react-icons/fi";
import {
  getProductByIdThunk,
  getProductWithPricingThunk,
  checkAvailabilityThunk,
  updateLocationStockThunk,
  transferStockThunk,
} from "../../../store/thunks/adminProductThunks";
import Button from "../../../components/common/Button";
import toast from "react-hot-toast";
import {
  ProductDetailsSkeleton,
  PricingTabSkeleton,
  StockTabSkeleton,
  HistoryTabSkeleton,
  OverviewTabSkeleton,
  InlineSpinner,
  EmptyState,
} from "./ProductDetailsSkeletons";

const ProductDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { product, productPricing, loading } = useSelector(
    (state) => state.adminProduct
  );
  const { user } = useSelector((state) => state.auth);
  const { branches, loading: branchLoading } = useSelector(
    (state) => state.branch
  );

  const [activeTab, setActiveTab] = useState("overview");
  const [selectedBranch, setSelectedBranch] = useState(
    user?.branchAccess?.primaryBranch || ""
  );
  const [showTransfer, setShowTransfer] = useState(false);
  const [pricingData, setPricingData] = useState(null);
  const [loadingPricing, setLoadingPricing] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(0);
  const [tabLoading, setTabLoading] = useState({});

  useEffect(() => {
    dispatch(getProductByIdThunk(id));
  }, [id, dispatch]);

  useEffect(() => {
    if (activeTab === "pricing" && product && !pricingData) {
      loadPricingData();
    }
  }, [activeTab, product]);

  const loadPricingData = async () => {
    setLoadingPricing(true);
    try {
      const result = await dispatch(
        getProductWithPricingThunk({
          id: product._id,
          options: {
            customerType: "RETAIL",
            quantity: 1,
            includAllBranches: true,
          },
        })
      ).unwrap();
      setPricingData(result.pricing || result.data?.pricing);
    } catch (error) {
      toast.error("Failed to load pricing");
    } finally {
      setLoadingPricing(false);
    }
  };

  const refreshProduct = async () => {
    try {
      await dispatch(getProductByIdThunk(id)).unwrap();
      toast.success("Product refreshed");
    } catch (error) {
      toast.error("Failed to refresh");
    }
  };

  const handleStockTransfer = async (fromBranch, toBranch, quantity) => {
    setTabLoading((prev) => ({ ...prev, transfer: true }));
    try {
      await dispatch(
        transferStockThunk({
          productId: product._id,
          fromBranch,
          toBranch,
          quantity,
        })
      ).unwrap();
      toast.success("Stock transferred successfully");
      dispatch(getProductByIdThunk(id));
    } catch (error) {
      toast.error("Transfer failed");
    } finally {
      setTabLoading((prev) => ({ ...prev, transfer: false }));
    }
  };

  // Show skeleton loader during loading
  if (loading) {
    return <ProductDetailsSkeleton />;
  }

  // No product found after loading
  if (!loading && !product) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FiPackage className="w-10 h-10 text-gray-400" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Product Not Found
          </h2>
          <p className="text-gray-500 mb-6">
            The product you're looking for doesn't exist or has been removed.
          </p>
          <Button onClick={() => navigate("/products")}>
            <FiArrowLeft className="w-4 h-4 mr-2" />
            Back to Products
          </Button>
        </div>
      </div>
    );
  }

  const getStockStatus = () => {
    const stock = product.stock?.availableQuantity || 0;
    const min = product.stock?.minStockLevel || 1;

    if (stock === 0)
      return {
        text: "Out of Stock",
        color: "red",
        bg: "bg-red-100",
        textColor: "text-red-700",
      };
    if (stock <= min)
      return {
        text: "Low Stock",
        color: "yellow",
        bg: "bg-yellow-100",
        textColor: "text-yellow-700",
      };
    return {
      text: "In Stock",
      color: "green",
      bg: "bg-green-100",
      textColor: "text-green-700",
    };
  };

  const stockStatus = getStockStatus();

  const getBranchName = (branchCode) => {
    const branch = branches?.find((b) => b.code === branchCode);
    return branch ? branch.name : branchCode;
  };

  const tabs = [
    { id: "overview", label: "Overview", icon: FiGrid },
    { id: "stock", label: "Stock", icon: FiBox },
    { id: "pricing", label: "Pricing", icon: FiDollarSign },
    { id: "history", label: "History", icon: FiClock },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Header */}
      <div className="lg:hidden sticky top-0 z-20 bg-white border-b shadow-sm">
        <div className="flex items-center justify-between p-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 -ml-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <FiArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="font-semibold text-gray-900 truncate max-w-[200px]">
            {product.name}
          </h1>
          <div className="flex items-center gap-1">
            <button
              onClick={refreshProduct}
              disabled={loading}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-500 disabled:opacity-50"
            >
              <FiRefreshCw
                className={`w-5 h-5 ${loading ? "animate-spin" : ""}`}
              />
            </button>
            <button
              onClick={() => navigate(`/products/edit/${id}`)}
              className="p-2 -mr-2 rounded-lg hover:bg-gray-100 transition-colors text-primary"
            >
              <FiEdit2 className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-4 lg:py-6 max-w-7xl">
        {/* Desktop Header */}
        <div className="hidden lg:flex items-center justify-between mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
          >
            <FiArrowLeft className="w-4 h-4 mr-2" />
            Back to Products
          </button>
          <button
            onClick={refreshProduct}
            disabled={loading}
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 disabled:opacity-50"
          >
            <FiRefreshCw
              className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
            />
            {loading ? "Refreshing..." : "Refresh"}
          </button>
        </div>

        {/* Product Hero Section */}
        <div className="bg-white rounded-xl lg:rounded-2xl shadow-sm overflow-hidden mb-4 lg:mb-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 lg:gap-6">
            {/* Image Gallery */}
            <div className="relative bg-gray-100">
              {/* Main Image */}
              <div className="aspect-square lg:aspect-[4/3] relative overflow-hidden">
                {product.media && product.media.length > 0 ? (
                  <img
                    src={product.media[selectedImage]?.url}
                    alt={product.media[selectedImage]?.altText || product.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gray-200">
                    <FiImage className="w-16 h-16 text-gray-400" />
                  </div>
                )}

                {/* Image Badges */}
                <div className="absolute top-3 left-3 flex flex-wrap gap-2">
                  {product.isNewArrival && (
                    <span className="px-2 py-1 text-xs font-medium bg-blue-500 text-white rounded-full">
                      New
                    </span>
                  )}
                  {product.isBestseller && (
                    <span className="px-2 py-1 text-xs font-medium bg-orange-500 text-white rounded-full">
                      Bestseller
                    </span>
                  )}
                  {product.isFeatured && (
                    <span className="px-2 py-1 text-xs font-medium bg-purple-500 text-white rounded-full">
                      Featured
                    </span>
                  )}
                  {product.isExclusive && (
                    <span className="px-2 py-1 text-xs font-medium bg-amber-500 text-white rounded-full">
                      Exclusive
                    </span>
                  )}
                </div>

                {/* Status Badge */}
                <div className="absolute top-3 right-3">
                  <span
                    className={`px-3 py-1.5 text-xs font-semibold rounded-full ${stockStatus.bg} ${stockStatus.textColor}`}
                  >
                    {stockStatus.text}
                  </span>
                </div>
              </div>

              {/* Thumbnail Strip */}
              {product.media && product.media.length > 1 && (
                <div className="flex gap-2 p-3 overflow-x-auto scrollbar-hide">
                  {product.media.map((media, index) => (
                    <button
                      key={media._id || index}
                      onClick={() => setSelectedImage(index)}
                      className={`flex-shrink-0 w-16 h-16 lg:w-20 lg:h-20 rounded-lg overflow-hidden border-2 transition-all ${
                        selectedImage === index
                          ? "border-primary ring-2 ring-primary/20"
                          : "border-transparent hover:border-gray-300"
                      }`}
                    >
                      <img
                        src={media.url}
                        alt={media.altText || `${product.name} ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Product Info */}
            <div className="p-4 lg:p-6 lg:py-8">
              {/* Title & IDs */}
              <div className="mb-4 lg:mb-6">
                <h1 className="text-xl lg:text-2xl xl:text-3xl font-bold text-gray-900 mb-2">
                  {product.name}
                </h1>
                <div className="flex flex-wrap items-center gap-2 lg:gap-3 text-sm text-gray-500">
                  <span className="bg-gray-100 px-2 py-1 rounded font-mono text-xs">
                    ID: {product.productId}
                  </span>
                  <span className="bg-gray-100 px-2 py-1 rounded font-mono text-xs">
                    SKU: {product.sku}
                  </span>
                  {product.hallmark?.isHallmarked && (
                    <span className="inline-flex items-center px-2 py-1 bg-yellow-100 text-yellow-700 text-xs rounded font-medium">
                      <FiShield className="w-3 h-3 mr-1" />
                      Hallmarked
                    </span>
                  )}
                </div>
              </div>

              {/* Quick Stats Grid */}
              <div className="grid grid-cols-2 gap-3 lg:gap-4 mb-4 lg:mb-6">
                <div className="bg-gradient-to-br from-amber-50 to-amber-100/50 rounded-xl p-3 lg:p-4">
                  <p className="text-xs text-amber-600 font-medium mb-1">
                    Type
                  </p>
                  <p className="text-sm lg:text-base font-semibold text-amber-900">
                    {product.type} - {product.subType}
                  </p>
                </div>
                <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-xl p-3 lg:p-4">
                  <p className="text-xs text-blue-600 font-medium mb-1">
                    Purity
                  </p>
                  <p className="text-sm lg:text-base font-semibold text-blue-900">
                    {product.metalDetails?.purity || product.purity}
                    {product.metalDetails?.color && (
                      <span className="text-xs font-normal ml-1 text-blue-600">
                        ({product.metalDetails.color})
                      </span>
                    )}
                  </p>
                </div>
                <div className="bg-gradient-to-br from-green-50 to-green-100/50 rounded-xl p-3 lg:p-4">
                  <p className="text-xs text-green-600 font-medium mb-1">
                    Gross Weight
                  </p>
                  <p className="text-sm lg:text-base font-semibold text-green-900">
                    {product.grossWeight}g
                  </p>
                </div>
                <div className="bg-gradient-to-br from-purple-50 to-purple-100/50 rounded-xl p-3 lg:p-4">
                  <p className="text-xs text-purple-600 font-medium mb-1">
                    Net Weight
                  </p>
                  <p className="text-sm lg:text-base font-semibold text-purple-900">
                    {product.netWeight}g
                  </p>
                </div>
              </div>

              {/* Stock Summary */}
              <div className="bg-gray-50 rounded-xl p-4 mb-4 lg:mb-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-gray-900 text-sm lg:text-base">
                    Stock Summary
                  </h3>
                  <span className="text-xs text-gray-500">
                    {product.stock?.locationStock?.length || 0} locations
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="text-center">
                    <p className="text-lg lg:text-xl font-bold text-gray-900">
                      {product.stock?.totalQuantity || 0}
                    </p>
                    <p className="text-xs text-gray-500">Total</p>
                  </div>
                  <div className="text-center border-x border-gray-200">
                    <p className="text-lg lg:text-xl font-bold text-green-600">
                      {product.stock?.availableQuantity || 0}
                    </p>
                    <p className="text-xs text-gray-500">Available</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg lg:text-xl font-bold text-orange-500">
                      {product.stock?.reservedQuantity || 0}
                    </p>
                    <p className="text-xs text-gray-500">Reserved</p>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="hidden lg:flex gap-3">
                <Button
                  variant="primary"
                  className="flex-1"
                  onClick={() => navigate(`/products/edit/${id}`)}
                >
                  <FiEdit2 className="w-4 h-4 mr-2" />
                  Edit Product
                </Button>
                <Button variant="outline">
                  <FiEye className="w-4 h-4 mr-2" />
                  Preview
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs Section */}
        <div className="bg-white rounded-xl lg:rounded-2xl shadow-sm overflow-hidden">
          {/* Tab Navigation */}
          <div className="border-b overflow-x-auto scrollbar-hide">
            <div className="flex min-w-max lg:min-w-0">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isLoading =
                  (tab.id === "pricing" && loadingPricing) ||
                  tabLoading[tab.id];
                return (
                  <button
                    key={tab.id}
                    className={`flex items-center gap-2 px-4 lg:px-6 py-3 lg:py-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                      activeTab === tab.id
                        ? "border-primary text-primary bg-primary/5"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                    }`}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    {isLoading ? (
                      <InlineSpinner size={16} className="text-current" />
                    ) : (
                      <Icon className="w-4 h-4" />
                    )}
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Tab Content */}
          <div className="p-4 lg:p-6">
            {activeTab === "overview" && (
              <ProductOverview
                product={product}
                branches={branches}
                getBranchName={getBranchName}
              />
            )}

            {activeTab === "stock" && (
              <StockManagement
                product={product}
                onTransfer={handleStockTransfer}
                selectedBranch={selectedBranch}
                onBranchChange={setSelectedBranch}
                branches={branches}
                getBranchName={getBranchName}
                isTransferring={tabLoading.transfer}
              />
            )}

            {activeTab === "pricing" && (
              <EnhancedPricingSection
                product={product}
                pricingData={pricingData || product.pricing}
                loading={loadingPricing}
                primaryBranch={user?.branchAccess?.primaryBranch}
                branches={branches}
                getBranchName={getBranchName}
                onRefresh={loadPricingData}
              />
            )}

            {activeTab === "history" && <ProductHistory product={product} />}
          </div>
        </div>
      </div>

      {/* Mobile Bottom Action Bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg p-4 z-20">
        <div className="flex gap-3">
          <Button
            variant="primary"
            className="flex-1"
            onClick={() => navigate(`/products/edit/${id}`)}
          >
            <FiEdit2 className="w-4 h-4 mr-2" />
            Edit Product
          </Button>
          <Button variant="outline" className="px-4">
            <FiEye className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Spacer for mobile bottom bar */}
      <div className="lg:hidden h-20"></div>
    </div>
  );
};

// Enhanced Pricing Section Component
const EnhancedPricingSection = ({
  product,
  pricingData,
  loading,
  primaryBranch,
  branches,
  getBranchName,
  onRefresh,
}) => {
  const [sortBy, setSortBy] = useState("price");
  const [sortOrder, setSortOrder] = useState("asc");
  const [expandedBranch, setExpandedBranch] = useState(null);

  // Include calculationDetails and applied pricing rule
  const branchComparison = useMemo(() => {
    if (!pricingData?.branches) return [];

    return Object.entries(pricingData.branches).map(([branchCode, pricing]) => {
      const branch = branches?.find((b) => b.code === branchCode);
      return {
        code: branchCode,
        name: branch?.name || branchCode,
        isPrimary: branchCode === primaryBranch,
        available: pricing.available,
        stock: pricing.stock,
        rates: pricing.rates,
        breakdown: pricing.breakdown,
        offers: pricing.offers || [],
        applied: pricing.applied,
        calculationDetails: pricing.calculationDetails,
        message: pricing.message,
      };
    });
  }, [pricingData?.branches, branches, primaryBranch]);

  const sortedBranches = useMemo(() => {
    const sorted = [...branchComparison];

    sorted.sort((a, b) => {
      if (a.isPrimary) return -1;
      if (b.isPrimary) return 1;

      switch (sortBy) {
        case "price":
          const priceA = a.breakdown?.finalAmount || Infinity;
          const priceB = b.breakdown?.finalAmount || Infinity;
          return sortOrder === "asc" ? priceA - priceB : priceB - priceA;

        case "stock":
          const stockA = a.stock?.available || 0;
          const stockB = b.stock?.available || 0;
          return sortOrder === "asc" ? stockA - stockB : stockB - stockA;

        case "name":
          return sortOrder === "asc"
            ? a.name.localeCompare(b.name)
            : b.name.localeCompare(a.name);

        default:
          return 0;
      }
    });

    return sorted;
  }, [branchComparison, sortBy, sortOrder, primaryBranch]);

  const getPriceVariation = (price, basePrice) => {
    if (!price || !basePrice) return null;
    const diff = price - basePrice;
    const percentage = (diff / basePrice) * 100;
    return {
      difference: diff,
      percentage: percentage.toFixed(1),
      isHigher: diff > 0,
      isLower: diff < 0,
    };
  };

  const getMetalRate = (branch) => {
    return branch.calculationDetails?.formulas?.metalValue?.rate || null;
  };

  const getMakingChargeInfo = (branch) => {
    const making = branch.calculationDetails?.formulas?.charges?.making;
    return making
      ? { type: making.type, rate: making.rate, amount: making.result }
      : null;
  };

  const getWastageInfo = (branch) => {
    const wastage = branch.calculationDetails?.formulas?.charges?.wastage;
    return wastage
      ? { type: wastage.type, rate: wastage.rate, amount: wastage.result }
      : null;
  };

  const getAdditionalCharges = (branch) => {
    return branch.calculationDetails?.formulas?.charges?.additional || [];
  };

  // Show skeleton during loading
  if (loading) {
    return <PricingTabSkeleton />;
  }

  if (!pricingData) {
    return (
      <EmptyState
        icon={FiInfo}
        title="No pricing data available"
        description="Click below to load pricing information"
        action={
          <Button variant="primary" size="sm" onClick={onRefresh}>
            <FiRefreshCw className="w-4 h-4 mr-2" />
            Load Pricing
          </Button>
        }
      />
    );
  }

  const lowestPrice = pricingData.summary?.lowestPrice;

  return (
    <div className="space-y-4 lg:space-y-6">
      {/* Customer Type Badge */}
      {pricingData.customerType && (
        <div className="flex items-center gap-2">
          <span className="px-3 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
            {pricingData.customerType} Pricing
          </span>
          <span className="text-xs text-gray-500">
            Total Stock: {pricingData.summary?.totalStockAcrossBranches || 0}{" "}
            units
          </span>
        </div>
      )}

      {/* Summary Cards */}
      {pricingData.summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 bg-green-500/10 rounded-lg flex items-center justify-center">
                <FiTrendingDown className="w-4 h-4 text-green-600" />
              </div>
            </div>
            <p className="text-xs text-green-600 font-medium">Lowest Price</p>
            <p className="text-lg lg:text-2xl font-bold text-green-900 mt-1">
              ₹{pricingData.summary.lowestPrice?.toLocaleString("en-IN")}
            </p>
          </div>

          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 bg-blue-500/10 rounded-lg flex items-center justify-center">
                <FiPercent className="w-4 h-4 text-blue-600" />
              </div>
            </div>
            <p className="text-xs text-blue-600 font-medium">Average Price</p>
            <p className="text-lg lg:text-2xl font-bold text-blue-900 mt-1">
              ₹
              {pricingData.summary.averagePrice
                ?.toFixed(2)
                ?.toLocaleString("en-IN")}
            </p>
          </div>

          <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 bg-orange-500/10 rounded-lg flex items-center justify-center">
                <FiTrendingUp className="w-4 h-4 text-orange-600" />
              </div>
            </div>
            <p className="text-xs text-orange-600 font-medium">Highest Price</p>
            <p className="text-lg lg:text-2xl font-bold text-orange-900 mt-1">
              ₹{pricingData.summary.highestPrice?.toLocaleString("en-IN")}
            </p>
          </div>

          <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 bg-purple-500/10 rounded-lg flex items-center justify-center">
                <FiLayers className="w-4 h-4 text-purple-600" />
              </div>
            </div>
            <p className="text-xs text-purple-600 font-medium">Price Range</p>
            <p className="text-lg lg:text-2xl font-bold text-purple-900 mt-1">
              ₹
              {(
                pricingData.summary.highestPrice -
                  pricingData.summary.lowestPrice || 0
              )
                .toFixed(2)
                .toLocaleString("en-IN")}
            </p>
          </div>
        </div>
      )}

      {/* Branch Comparison */}
      <div className="bg-white border rounded-xl overflow-hidden relative">
        {/* Header */}
        <div className="px-4 lg:px-6 py-4 bg-gray-50 border-b">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <h3 className="text-base lg:text-lg font-semibold text-gray-900">
              Branch-wise Pricing
            </h3>
            <div className="flex items-center gap-2">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="text-sm px-3 py-1.5 border rounded-lg bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary"
              >
                <option value="price">Sort by Price</option>
                <option value="stock">Sort by Stock</option>
                <option value="name">Sort by Name</option>
              </select>
              <button
                onClick={() =>
                  setSortOrder(sortOrder === "asc" ? "desc" : "asc")
                }
                className="p-2 border rounded-lg hover:bg-gray-100 transition-colors"
              >
                {sortOrder === "asc" ? (
                  <FiChevronUp className="w-4 h-4" />
                ) : (
                  <FiChevronDown className="w-4 h-4" />
                )}
              </button>
              <Button
                variant="outline"
                size="sm"
                onClick={onRefresh}
                className="hidden sm:flex"
              >
                <FiRefreshCw className="w-4 h-4 mr-1" />
                Refresh
              </Button>
            </div>
          </div>
        </div>

        {/* Desktop Table View */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Branch
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Stock
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Metal Rate
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Metal Value
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Making
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Wastage
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  GST
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Final Price
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Vs Lowest
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Offers
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedBranches.map((branch) => {
                const priceVariation =
                  branch.available && branch.breakdown
                    ? getPriceVariation(
                        branch.breakdown.finalAmount,
                        lowestPrice
                      )
                    : null;

                const metalRate = getMetalRate(branch);
                const makingInfo = getMakingChargeInfo(branch);
                const wastageInfo = getWastageInfo(branch);

                return (
                  <tr
                    key={branch.code}
                    className={`transition-colors ${
                      branch.isPrimary ? "bg-primary/5" : "hover:bg-gray-50"
                    }`}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-900">
                            {branch.name}
                          </span>
                          {branch.isPrimary && (
                            <span className="px-2 py-0.5 text-xs font-medium bg-primary text-white rounded">
                              Primary
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 font-mono mt-0.5">
                          {branch.code}
                        </div>
                        {branch.applied?.pricingRule && (
                          <div className="flex items-center gap-1 mt-1">
                            <FiFileText className="w-3 h-3 text-gray-400" />
                            <span className="text-[10px] text-gray-500">
                              {branch.applied.pricingRule.name}
                            </span>
                          </div>
                        )}
                      </div>
                    </td>

                    <td className="px-4 py-4 text-center">
                      {branch.available ? (
                        <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-full">
                          <FiCheckCircle className="w-3 h-3 mr-1" />
                          Available
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium bg-red-100 text-red-700 rounded-full">
                          <FiXCircle className="w-3 h-3 mr-1" />
                          N/A
                        </span>
                      )}
                    </td>

                    <td className="px-4 py-4 text-center">
                      {branch.available ? (
                        <div>
                          <div className="text-sm font-semibold text-gray-900">
                            {branch.stock?.available || 0}
                          </div>
                          {branch.stock?.reserved > 0 && (
                            <div className="text-xs text-orange-600">
                              {branch.stock.reserved} reserved
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>

                    <td className="px-4 py-4 text-right">
                      {branch.available && metalRate ? (
                        <span className="text-sm text-gray-700">
                          ₹{metalRate.toLocaleString("en-IN")}/g
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>

                    <td className="px-4 py-4 text-right">
                      {branch.available && branch.breakdown ? (
                        <span className="text-sm text-gray-700">
                          ₹
                          {branch.breakdown.metalValue?.toLocaleString("en-IN")}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>

                    <td className="px-4 py-4 text-right">
                      {branch.available && branch.breakdown ? (
                        <div>
                          <div className="text-sm text-gray-700">
                            ₹
                            {branch.breakdown.makingCharges?.toLocaleString(
                              "en-IN"
                            )}
                          </div>
                          {makingInfo && (
                            <div className="text-[10px] text-gray-500">
                              {makingInfo.type === "PERCENTAGE"
                                ? `${makingInfo.rate}%`
                                : `Fixed`}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>

                    <td className="px-4 py-4 text-right">
                      {branch.available && branch.breakdown ? (
                        <div>
                          {branch.breakdown.wastageCharges > 0 ? (
                            <>
                              <div className="text-sm text-gray-700">
                                ₹
                                {branch.breakdown.wastageCharges?.toLocaleString(
                                  "en-IN"
                                )}
                              </div>
                              {wastageInfo && (
                                <div className="text-[10px] text-gray-500">
                                  {wastageInfo.rate}%
                                </div>
                              )}
                            </>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>

                    <td className="px-4 py-4 text-right">
                      {branch.available && branch.breakdown ? (
                        <div>
                          <div className="text-sm text-gray-700">
                            ₹
                            {branch.breakdown.gst?.amount?.toLocaleString(
                              "en-IN"
                            )}
                          </div>
                          <div className="text-[10px] text-gray-500">
                            {branch.breakdown.gst?.rate}%
                          </div>
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>

                    <td className="px-4 py-4 text-right">
                      {branch.available && branch.breakdown ? (
                        <div>
                          <div className="text-sm font-bold text-primary">
                            ₹
                            {branch.breakdown.finalAmount?.toLocaleString(
                              "en-IN"
                            )}
                          </div>
                          {branch.breakdown.finalAmount === lowestPrice && (
                            <span className="text-xs text-green-600 font-medium">
                              Best Price
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>

                    <td className="px-4 py-4 text-center">
                      {priceVariation && priceVariation.difference !== 0 ? (
                        <div
                          className={`text-sm font-medium ${
                            priceVariation.isHigher
                              ? "text-red-600"
                              : "text-green-600"
                          }`}
                        >
                          {priceVariation.isHigher ? "+" : ""}
                          {priceVariation.percentage}%
                        </div>
                      ) : priceVariation ? (
                        <span className="text-xs text-gray-500">Base</span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>

                    <td className="px-4 py-4 text-center">
                      {branch.offers && branch.offers.length > 0 ? (
                        <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium bg-amber-100 text-amber-700 rounded-full">
                          <FiTag className="w-3 h-3 mr-1" />
                          {branch.offers.length}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="lg:hidden divide-y divide-gray-100">
          {sortedBranches.map((branch) => {
            const priceVariation =
              branch.available && branch.breakdown
                ? getPriceVariation(branch.breakdown.finalAmount, lowestPrice)
                : null;
            const isExpanded = expandedBranch === branch.code;

            const metalRate = getMetalRate(branch);
            const makingInfo = getMakingChargeInfo(branch);
            const wastageInfo = getWastageInfo(branch);
            const additionalCharges = getAdditionalCharges(branch);

            return (
              <div
                key={branch.code}
                className={`${branch.isPrimary ? "bg-primary/5" : ""}`}
              >
                <button
                  onClick={() =>
                    setExpandedBranch(isExpanded ? null : branch.code)
                  }
                  className="w-full p-4 flex items-center justify-between text-left"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-gray-900 text-sm">
                        {branch.name}
                      </span>
                      {branch.isPrimary && (
                        <span className="px-1.5 py-0.5 text-[10px] font-medium bg-primary text-white rounded">
                          Primary
                        </span>
                      )}
                      {!branch.available && (
                        <span className="px-1.5 py-0.5 text-[10px] font-medium bg-red-100 text-red-700 rounded">
                          N/A
                        </span>
                      )}
                    </div>
                    {branch.available && branch.breakdown && (
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-primary font-bold">
                          ₹
                          {branch.breakdown.finalAmount?.toLocaleString(
                            "en-IN"
                          )}
                        </span>
                        {priceVariation && priceVariation.difference !== 0 && (
                          <span
                            className={`text-xs ${
                              priceVariation.isHigher
                                ? "text-red-600"
                                : "text-green-600"
                            }`}
                          >
                            {priceVariation.isHigher ? "+" : ""}
                            {priceVariation.percentage}%
                          </span>
                        )}
                        {branch.breakdown.finalAmount === lowestPrice && (
                          <span className="text-xs text-green-600 bg-green-50 px-1.5 py-0.5 rounded">
                            Best
                          </span>
                        )}
                      </div>
                    )}
                    {branch.applied?.pricingRule && (
                      <div className="text-[10px] text-gray-500 mt-1">
                        Rule: {branch.applied.pricingRule.name}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {branch.available && (
                      <span className="text-xs text-gray-500">
                        Stock: {branch.stock?.available || 0}
                      </span>
                    )}
                    {isExpanded ? (
                      <FiChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <FiChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </button>

                {/* Expanded Details */}
                {isExpanded && branch.available && (
                  <div className="px-4 pb-4 space-y-3">
                    <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Metal Rate</span>
                        <span className="font-medium">
                          {metalRate
                            ? `₹${metalRate.toLocaleString("en-IN")}/g`
                            : "-"}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Metal Value</span>
                        <span className="font-medium">
                          ₹
                          {branch.breakdown?.metalValue?.toLocaleString(
                            "en-IN"
                          )}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">
                          Making Charges
                          {makingInfo && (
                            <span className="text-gray-400 ml-1">
                              ({makingInfo.rate}%)
                            </span>
                          )}
                        </span>
                        <span className="font-medium">
                          ₹
                          {branch.breakdown?.makingCharges?.toLocaleString(
                            "en-IN"
                          )}
                        </span>
                      </div>
                      {branch.breakdown?.wastageCharges > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">
                            Wastage
                            {wastageInfo && (
                              <span className="text-gray-400 ml-1">
                                ({wastageInfo.rate}%)
                              </span>
                            )}
                          </span>
                          <span className="font-medium">
                            ₹
                            {branch.breakdown?.wastageCharges?.toLocaleString(
                              "en-IN"
                            )}
                          </span>
                        </div>
                      )}
                      {additionalCharges.length > 0 &&
                        additionalCharges.map((charge, idx) => (
                          <div
                            key={idx}
                            className="flex justify-between text-sm"
                          >
                            <span className="text-gray-500">
                              {charge.name}
                              {charge.type === "PERCENTAGE" && (
                                <span className="text-gray-400 ml-1">
                                  ({charge.rate}%)
                                </span>
                              )}
                            </span>
                            <span className="font-medium">
                              ₹
                              {charge.breakdown?.result?.toLocaleString(
                                "en-IN"
                              )}
                            </span>
                          </div>
                        ))}
                      <div className="flex justify-between text-sm text-gray-400 pt-1 border-t border-dashed">
                        <span>Subtotal</span>
                        <span>
                          ₹{branch.breakdown?.subtotal?.toLocaleString("en-IN")}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">
                          GST ({branch.breakdown?.gst?.rate}%)
                        </span>
                        <span className="font-medium">
                          ₹
                          {branch.breakdown?.gst?.amount?.toLocaleString(
                            "en-IN"
                          )}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm pt-2 border-t">
                        <span className="font-semibold text-gray-900">
                          Final Price
                        </span>
                        <span className="font-bold text-primary">
                          ₹
                          {branch.breakdown?.finalAmount?.toLocaleString(
                            "en-IN"
                          )}
                        </span>
                      </div>
                    </div>

                    {branch.calculationDetails?.formulas && (
                      <div className="bg-blue-50 rounded-lg p-3">
                        <p className="text-xs font-medium text-blue-700 mb-2 flex items-center gap-1">
                          <FiInfo className="w-3 h-3" />
                          Calculation Details
                        </p>
                        <div className="space-y-1 text-xs text-blue-800">
                          <p>
                            {
                              branch.calculationDetails.formulas.metalValue
                                ?.calculation
                            }
                          </p>
                          <p>
                            {
                              branch.calculationDetails.formulas.charges?.making
                                ?.calculation
                            }
                          </p>
                          {branch.calculationDetails.formulas.charges
                            ?.wastage && (
                            <p>
                              {
                                branch.calculationDetails.formulas.charges
                                  .wastage.calculation
                              }
                            </p>
                          )}
                          <p>
                            {
                              branch.calculationDetails.formulas.gst
                                ?.calculation
                            }
                          </p>
                        </div>
                      </div>
                    )}

                    {branch.offers && branch.offers.length > 0 && (
                      <div className="bg-amber-50 rounded-lg p-3">
                        <p className="text-xs font-medium text-amber-700 mb-2">
                          Active Offers
                        </p>
                        {branch.offers.map((offer, idx) => (
                          <div
                            key={idx}
                            className="flex justify-between text-sm"
                          >
                            <span className="text-amber-800">{offer.name}</span>
                            <span className="font-medium text-amber-900">
                              {offer.discountType === "PERCENTAGE"
                                ? `${offer.discountValue}% OFF`
                                : `₹${offer.discountValue} OFF`}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Pricing Rules Legend */}
      {sortedBranches.some((b) => b.applied?.pricingRule?.name) && (
        <div className="bg-gray-50 rounded-xl p-4">
          <h4 className="text-sm font-medium text-gray-700 mb-3">
            Applied Pricing Rules
          </h4>
          <div className="flex flex-wrap gap-2">
            {[
              ...new Set(
                sortedBranches
                  .filter((b) => b.applied?.pricingRule?.name)
                  .map((b) => b.applied.pricingRule.name)
              ),
            ].map((ruleName, idx) => (
              <span
                key={idx}
                className="px-2 py-1 text-xs bg-white border rounded-lg text-gray-600"
              >
                {ruleName}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Product Overview Component
const ProductOverview = ({ product, branches, getBranchName }) => {
  return (
    <div className="space-y-4 lg:space-y-6">
      {/* Product Information */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
        {/* Basic Details */}
        <div className="bg-gray-50 rounded-xl p-4 lg:p-5">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <FiPackage className="w-4 h-4 text-primary" />
            Product Details
          </h3>
          <dl className="space-y-3">
            <div className="flex justify-between items-center">
              <dt className="text-sm text-gray-500">Type</dt>
              <dd className="text-sm font-medium text-gray-900">
                {product.type}
              </dd>
            </div>
            <div className="flex justify-between items-center">
              <dt className="text-sm text-gray-500">Sub Type</dt>
              <dd className="text-sm font-medium text-gray-900">
                {product.subType}
              </dd>
            </div>
            <div className="flex justify-between items-center">
              <dt className="text-sm text-gray-500">Purity</dt>
              <dd className="text-sm font-medium text-gray-900">
                {product.metalDetails?.purity} ({product.metalDetails?.karat}K)
              </dd>
            </div>
            <div className="flex justify-between items-center">
              <dt className="text-sm text-gray-500">Color</dt>
              <dd className="text-sm font-medium text-gray-900 capitalize">
                {product.metalDetails?.color?.toLowerCase()}
              </dd>
            </div>
            <div className="flex justify-between items-center">
              <dt className="text-sm text-gray-500">Gender</dt>
              <dd className="text-sm font-medium text-gray-900 capitalize">
                {product.gender?.toLowerCase()}
              </dd>
            </div>
            <div className="flex justify-between items-center">
              <dt className="text-sm text-gray-500">Status</dt>
              <dd>
                <span
                  className={`px-2 py-1 text-xs font-medium rounded-full ${
                    product.status === "ACTIVE"
                      ? "bg-green-100 text-green-700"
                      : "bg-gray-100 text-gray-700"
                  }`}
                >
                  {product.status}
                </span>
              </dd>
            </div>
          </dl>
        </div>

        {/* Weight & Stone Details */}
        <div className="bg-gray-50 rounded-xl p-4 lg:p-5">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <FiLayers className="w-4 h-4 text-primary" />
            Weight Details
          </h3>
          <dl className="space-y-3">
            <div className="flex justify-between items-center">
              <dt className="text-sm text-gray-500">Gross Weight</dt>
              <dd className="text-sm font-medium text-gray-900">
                {product.grossWeight}g
              </dd>
            </div>
            <div className="flex justify-between items-center">
              <dt className="text-sm text-gray-500">Net Weight</dt>
              <dd className="text-sm font-medium text-gray-900">
                {product.netWeight}g
              </dd>
            </div>
            <div className="flex justify-between items-center">
              <dt className="text-sm text-gray-500">Has Stones</dt>
              <dd className="text-sm font-medium text-gray-900">
                {product.hasStones ? "Yes" : "No"}
              </dd>
            </div>
            {product.hasStones && (
              <div className="flex justify-between items-center">
                <dt className="text-sm text-gray-500">Stone Weight</dt>
                <dd className="text-sm font-medium text-gray-900">
                  {product.totalStoneWeight}g
                </dd>
              </div>
            )}
            <div className="flex justify-between items-center">
              <dt className="text-sm text-gray-500">Hallmarked</dt>
              <dd>
                {product.hallmark?.isHallmarked ? (
                  <span className="inline-flex items-center text-xs font-medium text-yellow-700">
                    <FiShield className="w-3 h-3 mr-1" />
                    Yes
                  </span>
                ) : (
                  <span className="text-sm text-gray-500">No</span>
                )}
              </dd>
            </div>
          </dl>
        </div>
      </div>

      {/* Manufacturing Details */}
      <div className="bg-gray-50 rounded-xl p-4 lg:p-5">
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <FiSettings className="w-4 h-4 text-primary" />
          Manufacturing
        </h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <p className="text-xs text-gray-500 mb-1">Karigar Code</p>
            <p className="text-sm font-medium text-gray-900 font-mono">
              {product.manufacturing?.karigarCode || "-"}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Difficulty</p>
            <p className="text-sm font-medium text-gray-900 capitalize">
              {product.manufacturing?.difficultyLevel?.toLowerCase() || "-"}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Production Date</p>
            <p className="text-sm font-medium text-gray-900">
              {product.manufacturing?.productionDate
                ? new Date(
                    product.manufacturing.productionDate
                  ).toLocaleDateString()
                : "-"}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Customizable</p>
            <p className="text-sm font-medium text-gray-900">
              {product.customizable?.isCustomizable ? "Yes" : "No"}
            </p>
          </div>
        </div>
      </div>

      {/* Branch Availability */}
      <div className="bg-gray-50 rounded-xl p-4 lg:p-5">
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <FiMapPin className="w-4 h-4 text-primary" />
          Branch Availability
        </h3>
        <div className="flex flex-wrap gap-2">
          {product.branchInfo?.availableBranches?.map((branchCode) => (
            <span
              key={branchCode}
              className={`px-3 py-1.5 text-sm rounded-lg ${
                branchCode === product.branchInfo?.primaryBranch
                  ? "bg-primary text-white font-medium"
                  : "bg-white border text-gray-700"
              }`}
            >
              {getBranchName(branchCode)}
              {branchCode === product.branchInfo?.primaryBranch && (
                <span className="ml-1 text-xs opacity-80">(Primary)</span>
              )}
            </span>
          ))}
        </div>
      </div>

      {/* Categories */}
      {product.categories && product.categories.length > 0 && (
        <div className="bg-gray-50 rounded-xl p-4 lg:p-5">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <FiGrid className="w-4 h-4 text-primary" />
            Categories
          </h3>
          <div className="flex flex-wrap gap-2">
            {product.categories.map((category) => (
              <span
                key={category._id}
                className="px-3 py-1.5 text-sm bg-white border rounded-lg text-gray-700"
              >
                {category.name}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Visibility Settings */}
      <div className="bg-gray-50 rounded-xl p-4 lg:p-5">
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <FiEye className="w-4 h-4 text-primary" />
          Visibility & Access
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <div
            className={`p-3 rounded-lg text-center ${
              product.viewAccess?.includes("B2C_WEBSITE")
                ? "bg-green-100"
                : "bg-gray-100"
            }`}
          >
            <p className="text-xs font-medium text-gray-600">Website</p>
            <p
              className={`text-sm font-semibold mt-1 ${
                product.viewAccess?.includes("B2C_WEBSITE")
                  ? "text-green-700"
                  : "text-gray-400"
              }`}
            >
              {product.viewAccess?.includes("B2C_WEBSITE")
                ? "Visible"
                : "Hidden"}
            </p>
          </div>
          <div
            className={`p-3 rounded-lg text-center ${
              product.viewAccess?.includes("B2C_APP")
                ? "bg-green-100"
                : "bg-gray-100"
            }`}
          >
            <p className="text-xs font-medium text-gray-600">App</p>
            <p
              className={`text-sm font-semibold mt-1 ${
                product.viewAccess?.includes("B2C_APP")
                  ? "text-green-700"
                  : "text-gray-400"
              }`}
            >
              {product.viewAccess?.includes("B2C_APP") ? "Visible" : "Hidden"}
            </p>
          </div>
          <div
            className={`p-3 rounded-lg text-center ${
              product.isFeatured ? "bg-purple-100" : "bg-gray-100"
            }`}
          >
            <p className="text-xs font-medium text-gray-600">Featured</p>
            <p
              className={`text-sm font-semibold mt-1 ${
                product.isFeatured ? "text-purple-700" : "text-gray-400"
              }`}
            >
              {product.isFeatured ? "Yes" : "No"}
            </p>
          </div>
          <div
            className={`p-3 rounded-lg text-center ${
              product.isNewArrival ? "bg-blue-100" : "bg-gray-100"
            }`}
          >
            <p className="text-xs font-medium text-gray-600">New Arrival</p>
            <p
              className={`text-sm font-semibold mt-1 ${
                product.isNewArrival ? "text-blue-700" : "text-gray-400"
              }`}
            >
              {product.isNewArrival ? "Yes" : "No"}
            </p>
          </div>
          <div
            className={`p-3 rounded-lg text-center ${
              product.isBestseller ? "bg-orange-100" : "bg-gray-100"
            }`}
          >
            <p className="text-xs font-medium text-gray-600">Bestseller</p>
            <p
              className={`text-sm font-semibold mt-1 ${
                product.isBestseller ? "text-orange-700" : "text-gray-400"
              }`}
            >
              {product.isBestseller ? "Yes" : "No"}
            </p>
          </div>
        </div>
      </div>

      {/* Description */}
      {product.description && (
        <div className="bg-gray-50 rounded-xl p-4 lg:p-5">
          <h3 className="font-semibold text-gray-900 mb-3">Description</h3>
          <p className="text-sm text-gray-600 leading-relaxed">
            {product.description}
          </p>
        </div>
      )}
    </div>
  );
};

// Stock Management Component
const StockManagement = ({
  product,
  onTransfer,
  selectedBranch,
  onBranchChange,
  branches,
  getBranchName,
  isTransferring,
}) => {
  const [showTransferModal, setShowTransferModal] = useState(false);

  const totalStock = product.stock?.totalQuantity || 0;
  const availableStock = product.stock?.availableQuantity || 0;
  const reservedStock = product.stock?.reservedQuantity || 0;

  return (
    <div className="space-y-4 lg:space-y-6">
      {/* Stock Overview */}
      <div className="grid grid-cols-3 gap-3 lg:gap-4">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 text-center">
          <div className="w-10 h-10 lg:w-12 lg:h-12 bg-blue-500/10 rounded-full flex items-center justify-center mx-auto mb-2">
            <FiBox className="w-5 h-5 lg:w-6 lg:h-6 text-blue-600" />
          </div>
          <p className="text-xl lg:text-2xl font-bold text-blue-900">
            {totalStock}
          </p>
          <p className="text-xs text-blue-600 mt-1">Total Stock</p>
        </div>
        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 text-center">
          <div className="w-10 h-10 lg:w-12 lg:h-12 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-2">
            <FiCheckCircle className="w-5 h-5 lg:w-6 lg:h-6 text-green-600" />
          </div>
          <p className="text-xl lg:text-2xl font-bold text-green-900">
            {availableStock}
          </p>
          <p className="text-xs text-green-600 mt-1">Available</p>
        </div>
        <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-4 text-center">
          <div className="w-10 h-10 lg:w-12 lg:h-12 bg-orange-500/10 rounded-full flex items-center justify-center mx-auto mb-2">
            <FiClock className="w-5 h-5 lg:w-6 lg:h-6 text-orange-600" />
          </div>
          <p className="text-xl lg:text-2xl font-bold text-orange-900">
            {reservedStock}
          </p>
          <p className="text-xs text-orange-600 mt-1">Reserved</p>
        </div>
      </div>

      {/* Stock Levels */}
      <div className="bg-gray-50 rounded-xl p-4 lg:p-5">
        <h3 className="font-semibold text-gray-900 mb-4">Stock Levels</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-gray-500 mb-1">Minimum Stock Level</p>
            <p className="text-lg font-semibold text-gray-900">
              {product.stock?.minStockLevel || 1}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Maximum Stock Level</p>
            <p className="text-lg font-semibold text-gray-900">
              {product.stock?.maxStockLevel || "Unlimited"}
            </p>
          </div>
        </div>

        {/* Stock Progress Bar */}
        <div className="mt-4">
          <div className="flex justify-between text-xs text-gray-500 mb-2">
            <span>Stock Level</span>
            <span>
              {availableStock} / {product.stock?.maxStockLevel || "∞"}
            </span>
          </div>
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all ${
                availableStock === 0
                  ? "bg-red-500"
                  : availableStock <= (product.stock?.minStockLevel || 1)
                  ? "bg-yellow-500"
                  : "bg-green-500"
              }`}
              style={{
                width: product.stock?.maxStockLevel
                  ? `${Math.min(
                      (availableStock / product.stock.maxStockLevel) * 100,
                      100
                    )}%`
                  : "50%",
              }}
            />
          </div>
        </div>
      </div>

      {/* Location-wise Stock */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900">Location-wise Stock</h3>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowTransferModal(true)}
            disabled={isTransferring}
          >
            {isTransferring ? (
              <InlineSpinner size={16} className="mr-1" />
            ) : (
              <FiRefreshCw className="w-4 h-4 mr-1" />
            )}
            Transfer
          </Button>
        </div>

        <div className="space-y-3">
          {product.stock?.locationStock?.map((location, index) => (
            <div
              key={location._id || index}
              className="bg-white border rounded-xl p-4 hover:shadow-sm transition-shadow"
            >
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <FiMapPin className="w-4 h-4 text-gray-400" />
                    <span className="font-medium text-gray-900">
                      {getBranchName(location.branchCode)}
                    </span>
                    {location.branchCode ===
                      product.branchInfo?.primaryBranch && (
                      <span className="px-1.5 py-0.5 text-[10px] font-medium bg-primary text-white rounded">
                        Primary
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 font-mono mt-1">
                    {location.branchCode}
                  </p>
                  {location.storeCode && (
                    <p className="text-xs text-gray-500 mt-0.5">
                      Store: {location.storeCode}
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-4 sm:gap-6">
                  <div className="text-center">
                    <p className="text-lg font-bold text-gray-900">
                      {location.quantity}
                    </p>
                    <p className="text-xs text-gray-500">Total</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-green-600">
                      {location.available}
                    </p>
                    <p className="text-xs text-gray-500">Available</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-orange-500">
                      {location.reserved}
                    </p>
                    <p className="text-xs text-gray-500">Reserved</p>
                  </div>
                </div>
              </div>

              {/* Last Updated */}
              <div className="mt-3 pt-3 border-t flex items-center text-xs text-gray-500">
                <FiClock className="w-3 h-3 mr-1" />
                Last updated: {new Date(location.lastUpdated).toLocaleString()}
              </div>
            </div>
          ))}

          {(!product.stock?.locationStock ||
            product.stock.locationStock.length === 0) && (
            <EmptyState
              icon={FiBox}
              title="No location stock data"
              description="Stock information for locations is not available"
            />
          )}
        </div>
      </div>
    </div>
  );
};

// Product History Component
const ProductHistory = ({ product }) => {
  const historyItems = [
    {
      type: "created",
      icon: FiCheckCircle,
      iconBg: "bg-green-100",
      iconColor: "text-green-600",
      title: "Product Created",
      date: product.createdAt,
      user: product.createdBy?.adminName,
      role: product.createdBy?.role,
    },
  ];

  if (product.lastModifiedBy?.modifiedAt) {
    historyItems.push({
      type: "modified",
      icon: FiEdit2,
      iconBg: "bg-blue-100",
      iconColor: "text-blue-600",
      title: "Last Modified",
      date: product.lastModifiedBy.modifiedAt,
      user: product.lastModifiedBy.adminName,
      role: product.lastModifiedBy.role,
    });
  }

  return (
    <div className="space-y-4 lg:space-y-6">
      {/* Timeline */}
      <div className="relative">
        <div className="absolute left-5 top-0 bottom-0 w-px bg-gray-200 hidden sm:block" />

        <div className="space-y-4">
          {historyItems.map((item, index) => {
            const Icon = item.icon;
            return (
              <div key={index} className="relative flex gap-4">
                <div
                  className={`relative z-10 w-10 h-10 rounded-full ${item.iconBg} flex items-center justify-center flex-shrink-0`}
                >
                  <Icon className={`w-4 h-4 ${item.iconColor}`} />
                </div>
                <div className="flex-1 bg-gray-50 rounded-xl p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <div>
                      <h4 className="font-medium text-gray-900">
                        {item.title}
                      </h4>
                      <div className="flex items-center gap-2 mt-1">
                        <FiUser className="w-3 h-3 text-gray-400" />
                        <span className="text-sm text-gray-600">
                          {item.user}
                        </span>
                        {item.role && (
                          <span className="px-1.5 py-0.5 text-[10px] font-medium bg-gray-200 text-gray-600 rounded capitalize">
                            {item.role}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center text-sm text-gray-500">
                      <FiCalendar className="w-3 h-3 mr-1" />
                      {new Date(item.date).toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Product Stats */}
      {product.categories?.[0]?.stats && (
        <div className="bg-gray-50 rounded-xl p-4 lg:p-5">
          <h3 className="font-semibold text-gray-900 mb-4">
            Category Statistics
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="text-center p-3 bg-white rounded-lg">
              <p className="text-xl font-bold text-gray-900">
                {product.categories[0].stats.totalViews || 0}
              </p>
              <p className="text-xs text-gray-500 mt-1">Total Views</p>
            </div>
            <div className="text-center p-3 bg-white rounded-lg">
              <p className="text-xl font-bold text-gray-900">
                {product.categories[0].stats.totalSales || 0}
              </p>
              <p className="text-xs text-gray-500 mt-1">Total Sales</p>
            </div>
            <div className="text-center p-3 bg-white rounded-lg">
              <div className="flex items-center justify-center gap-1">
                <FiStar className="w-4 h-4 text-yellow-500" />
                <p className="text-xl font-bold text-gray-900">
                  {product.categories[0].stats.avgRating || 0}
                </p>
              </div>
              <p className="text-xs text-gray-500 mt-1">Avg Rating</p>
            </div>
            <div className="text-center p-3 bg-white rounded-lg">
              <p className="text-xl font-bold text-gray-900">
                {product.categories[0].stats.activeProducts || 0}
              </p>
              <p className="text-xs text-gray-500 mt-1">Active in Category</p>
            </div>
          </div>
        </div>
      )}

      {/* SEO Info */}
      {product.productSeo && (
        <div className="bg-gray-50 rounded-xl p-4 lg:p-5">
          <h3 className="font-semibold text-gray-900 mb-4">SEO Information</h3>
          <dl className="space-y-3">
            <div>
              <dt className="text-xs text-gray-500 mb-1">SEO Title</dt>
              <dd className="text-sm text-gray-900">
                {product.productSeo.basicSeo?.seoTitle || "-"}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500 mb-1">URL Slug</dt>
              <dd className="text-sm text-gray-900 font-mono bg-gray-100 px-2 py-1 rounded inline-block">
                /{product.productSeo.basicSeo?.urlSlug || "-"}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500 mb-1">Primary Keyword</dt>
              <dd className="text-sm text-gray-900">
                {product.productSeo.basicSeo?.primaryKeyword || "-"}
              </dd>
            </div>
          </dl>
        </div>
      )}
    </div>
  );
};

export default ProductDetails;
